import { useEffect, useMemo, useState } from 'react';
import { getPaymentSettings, savePaymentSettings } from '../../api/adminPaymentSettings';
import { useToast } from '../../components/ToastContext';

const defaults = {
  currency: 'INR',
  platformFeeBps: 1000,
  payoutTrigger: 'AFTER_CHECKOUT',
  payoutDelayHours: 0,
  minPayoutAmount: 0,
  autoRefundEnabled: false,
};

const payoutOptions = [
  { value: 'AFTER_CHECKIN', label: 'After check-in' },
  { value: 'AFTER_CHECKOUT', label: 'After check-out' },
];

const formatPercent = (bps) => `${(Number(bps || 0) / 100).toFixed(2)}%`;

const AdminPaymentSettings = ({ token }) => {
  const toast = useToast();
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPaymentSettings(token);
      if (data) {
        setForm({
          currency: data.currency || 'INR',
          platformFeeBps: data.platformFeeBps ?? defaults.platformFeeBps,
          payoutTrigger: data.payoutTrigger || defaults.payoutTrigger,
          payoutDelayHours: data.payoutDelayHours ?? 0,
          minPayoutAmount: data.minPayoutAmount ?? 0,
          autoRefundEnabled: !!data.autoRefundEnabled,
        });
        setUpdatedAt(data.updatedAt || '');
      }
    } catch (err) {
      setError(err.message || 'Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validationError = useMemo(() => {
    if (!form.currency?.trim()) return 'Currency is required';
    if (Number(form.platformFeeBps) < 0 || Number(form.platformFeeBps) > 10000) {
      return 'Platform fee must be between 0 and 10000 bps';
    }
    if (!['AFTER_CHECKIN', 'AFTER_CHECKOUT'].includes(form.payoutTrigger)) {
      return 'Select a valid payout trigger';
    }
    if (Number(form.payoutDelayHours) < 0) return 'Payout delay must be 0 or higher';
    if (Number(form.minPayoutAmount) < 0) return 'Minimum payout must be 0 or higher';
    return '';
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validationError) {
      toast?.error?.(validationError);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        currency: form.currency.trim().toUpperCase(),
        platformFeeBps: Number(form.platformFeeBps),
        payoutTrigger: form.payoutTrigger,
        payoutDelayHours: Number(form.payoutDelayHours),
        minPayoutAmount: Number(form.minPayoutAmount),
        autoRefundEnabled: !!form.autoRefundEnabled,
      };
      const saved = await savePaymentSettings(token, payload);
      if (saved) {
        setForm({
          currency: saved.currency || payload.currency,
          platformFeeBps: saved.platformFeeBps ?? payload.platformFeeBps,
          payoutTrigger: saved.payoutTrigger || payload.payoutTrigger,
          payoutDelayHours: saved.payoutDelayHours ?? payload.payoutDelayHours,
          minPayoutAmount: saved.minPayoutAmount ?? payload.minPayoutAmount,
          autoRefundEnabled: !!saved.autoRefundEnabled,
        });
      }
      setUpdatedAt(saved?.updatedAt || '');
      toast?.success?.('Payment settings saved');
    } catch (err) {
      toast?.error?.(err.message || 'Failed to save payment settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="admin-page-title text-2xl font-semibold text-slate-900">Payment settings</h1>
          <p className="text-sm text-slate-600">Configure platform fees and payout rules.</p>
        </div>
        {updatedAt && (
          <span className="text-xs text-slate-500">Updated {new Date(updatedAt).toLocaleString()}</span>
        )}
      </div>

      {error && <div className="rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="admin-card rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
          Loading payment settings...
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="admin-card rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Currency</span>
              <input
                value={form.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="INR"
                required
              />
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Platform fee (bps)</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="10000"
                  value={form.platformFeeBps}
                  onChange={(e) => handleChange('platformFeeBps', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                />
                <span className="text-xs text-slate-500">{formatPercent(form.platformFeeBps)}</span>
              </div>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Payout trigger</span>
              <select
                value={form.payoutTrigger}
                onChange={(e) => handleChange('payoutTrigger', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                {payoutOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Payout delay (hours)</span>
              <input
                type="number"
                min="0"
                value={form.payoutDelayHours}
                onChange={(e) => handleChange('payoutDelayHours', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Minimum payout amount</span>
              <input
                type="number"
                min="0"
                value={form.minPayoutAmount}
                onChange={(e) => handleChange('minPayoutAmount', e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.autoRefundEnabled}
                onChange={(e) => handleChange('autoRefundEnabled', e.target.checked)}
              />
              Auto refund enabled
            </label>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Changes apply to new payouts and refunds once saved.
            </p>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminPaymentSettings;
